import React, { useEffect } from 'react';
import { FiMic, FiMicOff } from 'react-icons/fi';
import { useSpeechToText, LangCode } from '../hooks/useSpeechToText';

type MicButtonProps = {
  onFinal: (text: string) => void;
  onInterim?: (text: string) => void;
  onError?: (message: string | null) => void;
  lang?: LangCode;
  disabled?: boolean;
  className?: string;
};

const MicButton: React.FC<MicButtonProps> = ({
  onFinal,
  onInterim,
  onError,
  lang = 'ar-PS',
  disabled = false,
  className,
}) => {
  const {
    start,
    stop,
    reset,
    listening,
    transcript,
    interim,
    error,
    supported,
    setLang,
  } = useSpeechToText({ lang });

  useEffect(() => {
    setLang(lang);
  }, [lang, setLang]);

  useEffect(() => {
    if (typeof onInterim === 'function') {
      onInterim(interim);
    }
  }, [interim, onInterim]);

  useEffect(() => {
    if (typeof onError === 'function') {
      onError(error);
    }
  }, [error, onError]);

  useEffect(() => {
    if (!listening && transcript.trim().length > 0) {
      onFinal(transcript.trim());
      if (typeof onInterim === 'function') {
        onInterim('');
      }
      reset();
    }
  }, [listening, transcript, onFinal, onInterim, reset]);

  useEffect(() => {
    if (disabled && listening) {
      stop();
    }
  }, [disabled, listening, stop]);

  if (!supported) {
    const classes = ['text-xs text-red-500/80 italic'];
    if (className) classes.push(className);
    return <div className={classes.join(' ')}>Microphone not supported in this browser.</div>;
  }

  const handleToggle = () => {
    if (disabled) return;
    if (listening) {
      stop();
    } else {
      start();
    }
  };

  const title = listening ? 'Stop Voice Input' : 'Start Voice Input';

  const wrapperClasses = ['flex flex-col items-center gap-1'];
  if (className) wrapperClasses.push(className);

  const buttonClasses = [
    'rounded-full border-2 p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400',
  ];
  if (listening) {
    buttonClasses.push('border-blue-600 bg-blue-600 text-white animate-pulse');
  } else {
    buttonClasses.push('border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:text-blue-600');
  }
  if (disabled) {
    buttonClasses.push('opacity-60 cursor-not-allowed');
  }

  return (
    <div className={wrapperClasses.join(' ')}>
      <button
        type="button"
        onClick={handleToggle}
        aria-pressed={listening}
        title={title}
        className={buttonClasses.join(' ')}
        disabled={disabled}
      >
        {listening ? <FiMicOff className="w-4 h-4" /> : <FiMic className="w-4 h-4" />}
      </button>
      {error && (
        <span className="text-[11px] text-red-500/80 text-center px-1 max-w-[8rem] leading-tight">
          {error}
        </span>
      )}
    </div>
  );
};

export default MicButton;
